// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a drawer component for displaying a note's audit log.
 * It shows a chronological list of all changes and actions performed on the note.
 */
import { AuditDrawer } from '@/components/audit-drawer';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { NoteAuditList } from '@/note/components/note-audit-list';
import { Suspense } from 'react';

interface Props {
  /**
   * The key of the note whose audit events should be displayed
   */
  noteKey: string;

  /**
   * Object containing the drawer's open state and handlers
   */
  disclosure: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };
}

/**
 * A drawer component that displays a note's audit log.
 * Shows a chronological list of all changes and actions performed on the note.
 */
export function NoteAuditDrawer(props: Props) {
  return (
    <AuditDrawer
      title="Note audit log"
      disclosure={props.disclosure}
      auditLog={
        <SupportErrorBoundary message="An error occurred while loading the audit log.">
          <Suspense>
            <NoteAuditList noteKey={props.noteKey} />
          </Suspense>
        </SupportErrorBoundary>
      }
    />
  );
}
